import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuBell, LuCalendarCheck, LuHouse, LuLayoutGrid, LuMail, LuMenu, LuSearch, LuX } from 'react-icons/lu';
import MissedCallsBell from './MissedCalls/MissedCallsBell';
import usePresenceHeartbeat from '../hooks/usePresenceHeartbeat';
import useSignOut from '../hooks/useSignOut';
import useUnseenTaskCount from '../hooks/useUnseenTaskCount';
import { useUserProfile } from '../contexts/UserProfileContext';
import { usePendingLeaves } from '../contexts/PendingLeavesContext';
import { useMessageNotifications } from './MessageNotifications';
import MyTasksModal from '../pages/Dashboard/MyTasksModal';
import IconButton from './ui/IconButton';
import { GLASS_PANEL } from './ui/glass';
import { getRouteMeta } from './shell/menuConfig';
import useProfileImage from './shell/useProfileImage';
import TopBarSearch from './shell/TopBarSearch';
import ThemeToggleButton from './shell/ThemeToggleButton';
import UserMenu from './shell/UserMenu';
import LanguageSwitcher from './shell/LanguageSwitcher';
import { useTranslation } from '../i18n/LanguageContext';

const TASKS_POLL_MS = 60000;

/**
 * Floating glass top bar: page title, menu search, notifications and the
 * account menu. The glass is a separate layer behind the content so that the
 * dropdowns can blur the page themselves (a backdrop-filter ancestor would
 * confine their blur to the bar).
 */
const TopBar = ({ pageTitle = '', onOpenMobileNav }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, tMenu, language } = useTranslation();
  const { profileImageUrl, employeeData, employeeId, refetchProfileData } = useUserProfile();
  const { pendingLeaveCount } = usePendingLeaves();
  const { unreadCount: unreadMessageCount } = useMessageNotifications();
  const { unseenCount, refresh: refreshUnseenCount } = useUnseenTaskCount(employeeId, TASKS_POLL_MS);
  const { displayUrl, handleImageError } = useProfileImage(profileImageUrl, refetchProfileData);
  const { signOutUser, isSigningOut } = useSignOut();
  const [showMyTasks, setShowMyTasks] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Presence heartbeat + current route for "Çevrimiçi Kullanıcılar"
  // (shared with the Hall, see hooks/usePresenceHeartbeat.js).
  usePresenceHeartbeat(employeeId);

  const routeMeta = getRouteMeta(pathname);
  const title = pageTitle || (routeMeta ? tMenu(routeMeta.path, routeMeta.title) : '');
  const group = routeMeta?.group ? tMenu(routeMeta.groupPath, routeMeta.group) : null;

  // Below xl the inline search (which owns Ctrl/⌘+K) is hidden; open the row instead
  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && !window.matchMedia('(min-width: 1280px)').matches) {
        event.preventDefault();
        setMobileSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  const handleCloseMyTasks = () => {
    setShowMyTasks(false);
    refreshUnseenCount();
  };

  return (
    <>
      <header lang={language} className="tw-pointer-events-none tw-sticky tw-top-0 tw-z-[998] tw-px-3 tw-pt-3 tw-font-sans tw-antialiased sm:tw-px-5 md:tw-pl-0">
        <div className="tw-pointer-events-auto tw-relative tw-isolate tw-flex tw-h-16 tw-items-center tw-gap-2 tw-rounded-[1.4rem] tw-px-2.5 sm:tw-gap-3 sm:tw-px-3">
          <div aria-hidden="true" className={`tw-absolute tw-inset-0 -tw-z-10 tw-rounded-[inherit] ${GLASS_PANEL}`} />

          <IconButton label={t('shell.openMenu')} onClick={onOpenMobileNav} className="md:tw-hidden">
            <LuMenu aria-hidden="true" className="tw-h-5 tw-w-5" />
          </IconButton>

          <div className="tw-min-w-0 tw-flex-1 xl:tw-max-w-[18rem] xl:tw-flex-none">
            {group && (
              <p className="tw-hidden tw-truncate tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-slate-400 sm:tw-block dark:tw-text-slate-500">
                {group}
              </p>
            )}
            <h1 className="tw-truncate tw-text-[15px] tw-font-semibold tw-tracking-tight tw-text-slate-900 sm:tw-text-base dark:tw-text-white">
              {title}
            </h1>
          </div>

          <TopBarSearch withShortcut className="tw-hidden tw-min-w-0 tw-max-w-md tw-flex-1 xl:tw-block" />

          <div className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5 sm:tw-gap-2">
            <IconButton
              label={mobileSearchOpen ? t('topbar.search.close') : t('topbar.search.open')}
              onClick={() => setMobileSearchOpen((value) => !value)}
              className="xl:tw-hidden"
            >
              {mobileSearchOpen
                ? <LuX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
                : <LuSearch aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
            </IconButton>
            <IconButton label={t('shell.hall')} onClick={() => navigate('/hall')} className="tw-hidden lg:tw-grid">
              <LuLayoutGrid aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </IconButton>
            <IconButton label={t('shell.home')} onClick={() => navigate('/dashboard')} className="tw-hidden lg:tw-grid">
              <LuHouse aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </IconButton>

            {/* Cevapsız aramalar — yalnızca cevapsız arama varken görünür */}
            <MissedCallsBell />

            <IconButton
              label={unseenCount > 0 ? t('topbar.newTasks', { count: unseenCount }) : t('topbar.tasks')}
              badge={unseenCount}
              tone={unseenCount > 0 ? 'accent' : 'default'}
              onClick={() => setShowMyTasks(true)}
            >
              <LuBell aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </IconButton>

            {unreadMessageCount > 0 && (
              <IconButton
                label={t('topbar.unreadMessages')}
                badge={unreadMessageCount}
                tone="alert"
                onClick={() => navigate('/profile', { state: { activeTab: 'mesajlar', requestedAt: Date.now() } })}
              >
                <LuMail aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
              </IconButton>
            )}

            {pendingLeaveCount > 0 && (
              <IconButton
                label={t('topbar.pendingLeaves')}
                badge={pendingLeaveCount}
                tone="alert"
                onClick={() => navigate('/izin-yonetimi')}
              >
                <LuCalendarCheck aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
              </IconButton>
            )}

            <LanguageSwitcher layoutId="topbar-language-pill" className="tw-hidden lg:tw-inline-flex" />
            <ThemeToggleButton className="tw-hidden lg:tw-grid" />

            <UserMenu
              employeeData={employeeData}
              imageUrl={displayUrl}
              onImageError={handleImageError}
              onSignOut={signOutUser}
              isSigningOut={isSigningOut}
            />
          </div>
        </div>

        {mobileSearchOpen && (
          <div className="tw-pointer-events-auto tw-relative tw-isolate tw-mt-2 tw-rounded-[1.4rem] tw-p-2 xl:tw-hidden">
            <div aria-hidden="true" className={`tw-absolute tw-inset-0 -tw-z-10 tw-rounded-[inherit] ${GLASS_PANEL}`} />
            <TopBarSearch autoFocus onDone={() => setMobileSearchOpen(false)} />
          </div>
        )}
      </header>

      {/* Portals itself to <body> (ui/Modal), above the sidebar and the bar */}
      <MyTasksModal isOpen={showMyTasks} onClose={handleCloseMyTasks} />
    </>
  );
};

TopBar.propTypes = {
  pageTitle: PropTypes.string,
  onOpenMobileNav: PropTypes.func.isRequired,
};

export default TopBar;
